import { UI_CONTEXT_REVIEW_LESSON, UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL } from 'TutorBotConversation';
import {
    type ContentInfoForLessonPlayerAlgorithm,
    type ContentInfoForReviewLessonUiContext,
    type ContentInfoForReviewPreviousMaterialUiContext,
    type LessonPlayerBotChatUiContext,
    type LessonPlayerBotContentInfo,
} from '../TutorBotAiTutorChat.types';
import {
    isLessonPlayerAlgorithmUiContext,
    isReviewPreviousMaterialAlgorithmUiContext,
} from './LessonPlayerBotUiContext';

/*
    ContentInfo is generated in the player view model and passed into LessonPlayerBot.

    LessonPlayerBot uses the ContentInfo to generate attributes that get passed to the tutorbot
    service. In the case of text chat, this happens in getTutorBotChatClientContext. In the case of audio
    chat, this is in getVoiceAgentAttrs.
*/

type GetBotContentInfoProps = {
    uiContext: LessonPlayerBotChatUiContext;
    lessonId: string;
    previousLessonId: string | null;
    streamId: string | null;
    lessonVersionId: string;
    lessonTitle: string;
};

export function getContentInfo({
    uiContext,
    lessonId,
    streamId,
    lessonVersionId,
    lessonTitle,
    previousLessonId,
}: GetBotContentInfoProps): LessonPlayerBotContentInfo {
    if (isLessonPlayerAlgorithmUiContext(uiContext)) {
        return getLessonPlayerAlgorithmContentInfo({ lessonId, lessonTitle, lessonVersionId, streamId });
    }

    if (isReviewPreviousMaterialAlgorithmUiContext(uiContext)) {
        if (uiContext === UI_CONTEXT_REVIEW_LESSON) {
            return getReviewLessonContentInfo({ lessonId, streamId });
        }

        if (uiContext === UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL) {
            return getReviewPreviousMaterialContentInfo({ lessonId, previousLessonId, streamId });
        }
    }

    throw new Error(`Unsupported uiContext: ${uiContext}`);
}

function getLessonPlayerAlgorithmContentInfo({
    lessonId,
    lessonTitle,
    lessonVersionId,
    streamId,
}: Pick<GetBotContentInfoProps, 'lessonId' | 'lessonTitle' | 'lessonVersionId' | 'streamId'>) {
    const contentInfo: ContentInfoForLessonPlayerAlgorithm = {
        lessonId,
        lessonTitle,
        lessonVersionId,
        streamId,
    };
    return contentInfo;
}

function getReviewLessonContentInfo({ lessonId, streamId }: Pick<GetBotContentInfoProps, 'lessonId' | 'streamId'>) {
    if (!streamId) {
        throw new Error(`streamId is required to build content info for ${UI_CONTEXT_REVIEW_LESSON} uiContext`);
    }
    const contentInfo: ContentInfoForReviewLessonUiContext = {
        targetLesson: 'this_lesson',
        lessonId,
        streamId,
    };
    return contentInfo;
}

function getReviewPreviousMaterialContentInfo({
    lessonId,
    previousLessonId,
    streamId,
}: Pick<GetBotContentInfoProps, 'lessonId' | 'previousLessonId' | 'streamId'>) {
    if (!streamId) {
        throw new Error(
            `streamId is required to build content info for ${UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL} uiContext`,
        );
    }
    if (!previousLessonId) {
        throw new Error(
            `previousLessonId is required to build content info for ${UI_CONTEXT_REVIEW_PREVIOUS_MATERIAL} uiContext`,
        );
    }
    const contentInfo: ContentInfoForReviewPreviousMaterialUiContext = {
        targetLesson: 'previous_lesson',
        lessonId,
        streamId,
        targetLessonId: previousLessonId,
    };
    return contentInfo;
}
